import axios from 'axios';
import { customEvent } from '@jspartials/general-utils';
import { gtmCustomEvent } from '@jspartials/gtm-utils';
import {
  getCookie,
  COOKIE_NAME_FOR_GTM_CUSTOM_SESSION_ID
} from '@jspartials/cookie-utils';

// Selectors
const selector = {
  pdp: {
    wrapper: '[data-action="Product-Show"]',
    buttonAddToCart: '.js-button-add-to-cart'
  },
  productTileVariant: '.js-product-color-variants__list-item'
};

// Variables
const apiUrl = 'https://relevancer.hawkersco.com/e';
const localeCookieName = 'HW_preferred_locale';
const defaultLocale = 'en';
const eventName = {
  productVariantClick: '1',
  pdpView: '2',
  addToCart: '3'
};
const listOfProductTileVariantsViewed = [];

// Functions

/**
 * Retrieves the locale from the cookie
 * @returns {string} - Returns the locale code from the cookie, or 'en'
 */
const getLocaleFromCookie = () => {
  const localeCookieValue = getCookie(localeCookieName);

  return localeCookieValue ? localeCookieValue : defaultLocale;
};

/**
 * Formats the event data for API consumption
 * @param {object} event - The event data to format
 * @returns {string} - The formatted event string
 */
const formatEventForApi = (event) => {
  const locale = getLocaleFromCookie();
  const customSessionId = getCookie(COOKIE_NAME_FOR_GTM_CUSTOM_SESSION_ID);

  return customSessionId
    ? `${customSessionId}|${locale}|${event.sku}|${event.name}`
    : null;
};

/**
 * Sends the formatted event data to the API
 * @param {object} event - The event data to send
 */
const sendEventToApi = (event) => {
  const formattedEvent = formatEventForApi(event);

  if (formattedEvent) {
    axios
      .post(apiUrl, formattedEvent, {
        'Content-Type': 'text/plain'
      })
      // .then((response) => {
      //   console.log(`Event sent successfully: ${response.data}`);
      // })
      .catch((error) => {
        console.error(`Error sending event to API: ${error}`);
      });
  }
};

/**
 * Checks if the product detail page is loaded
 * @returns {boolean} - Returns true if the PDP is loaded, otherwise false
 */
const pdpIsLoaded = () => {
  return document.querySelector(selector.pdp.wrapper) || false;
};

/**
 * Retrieves the product ID from the product detail page
 * @returns {string} - Returns the product ID if found, otherwise an empty string
 */
const getProductIdFromPdp = () => {
  const pdpButtonAddToCart = document.querySelector(
    selector.pdp.buttonAddToCart
  );
  const productId = pdpButtonAddToCart?.dataset?.pid || '';

  return productId;
};

/**
 * Launches a PDP view event if the product ID is available
 * @returns {void}
 */
const launchPdpViewEvent = () => {
  const productId = getProductIdFromPdp();

  if (productId) {
    const event = {
      name: eventName.pdpView,
      sku: productId
    };

    sendEventToApi(event);
  }
};

/**
 * Launches an add to cart event with the given product ID
 * @param {string} productId - The ID of the product being added to the cart
 * @returns {void}
 */
const launchAddToCartEvent = (productId) => {
  const event = {
    name: eventName.addToCart,
    sku: productId
  };
  sendEventToApi(event);
};

/**
 * Listens for clicks on product variant tiles and saves the event to local storage
 * @returns {void}
 */
const listenForProductVariantClick = () => {
  const productTileVariants =
    document.querySelectorAll(selector.productTileVariant) || [];

  productTileVariants.forEach((variant) => {
    const productId = variant?.dataset?.pid || '';
    if (productId) {
      if (
        !pdpIsLoaded() &&
        !isViewed(productId, listOfProductTileVariantsViewed)
      ) {
        variant.addEventListener('click', () => {
          const event = {
            name: eventName.productVariantClick,
            sku: productId
          };
          sendEventToApi(event);
        });

        listOfProductTileVariantsViewed.push(productId);
      }
    }
  });
};

/**
 * Determine if the element is already viewed
 * @param {HTMLElement} element - The element to check
 * @param {string} string - The string to check against the list
 * @returns {Boolean} - True if the element is already viewed, otherwise false
 */
const isViewed = (string, list) => list.includes(string);

// Initialize the script when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', function () {
  // Listen for product detail page view
  if (pdpIsLoaded()) {
    launchPdpViewEvent();
  }

  // Listen for product variant clicks
  listenForProductVariantClick();
});

// Listen for updates to the product detail page content
document.addEventListener('afterUpdateContentOfProductDetailPage', function () {
  launchPdpViewEvent();
});

// Listen for add to cart
document.addEventListener(gtmCustomEvent.afterAddToCart, (event) => {
  const details = event.detail;
  const productId = details?.productId || null;

  if (productId) {
    launchAddToCartEvent(productId);
  }
});

// Listen for product tiles loaded
document.addEventListener(customEvent.listenProductTiles, () => {
  listenForProductVariantClick();
});
